// MindForge web — TypeScript port of logic/math_problems.py.
//
// True/False math verification problems for the Dual Task mode.
// Operand range scales with difficulty:
//   1-4: 2-9    5-7: 2-15    8+: 5-20

const OPERATORS = ["+", "-", "x"] as const;
type Operator = (typeof OPERATORS)[number];

/** Integer in [min, max] inclusive — matches Python's random.randint. */
function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randChoice<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** A True/False math verification problem. */
export class MathProblem {
  readonly text: string;
  readonly answer: boolean;
  playerAnswer: boolean | null = null;

  constructor(
    text: string,
    answer: boolean,
    playerAnswer: boolean | null = null,
  ) {
    this.text = text;
    this.answer = answer;
    this.playerAnswer = playerAnswer;
  }

  /** True only when the player answered AND the answer matches. */
  get answeredCorrectly(): boolean {
    // Python: `self.player_answer == self.answer` returns False for None.
    return this.playerAnswer === this.answer;
  }
}

/** Generate a random True/False arithmetic problem at the given difficulty. */
export function generateMathProblem(difficulty = 1): MathProblem {
  let a: number;
  let b: number;
  if (difficulty >= 8) {
    a = randInt(5, 20);
    b = randInt(5, 20);
  } else if (difficulty >= 5) {
    a = randInt(2, 15);
    b = randInt(2, 15);
  } else {
    a = randInt(2, 9);
    b = randInt(2, 9);
  }

  const op: Operator = randChoice(OPERATORS);
  let correctVal: number;
  if (op === "+") correctVal = a + b;
  else if (op === "-") correctVal = a - b;
  else correctVal = a * b;

  const isTrue = Math.random() < 0.5;
  const shownVal = isTrue
    ? correctVal
    : correctVal + randChoice([-2, -1, 1, 2]);

  return new MathProblem(`${a} ${op} ${b} = ${shownVal}`, isTrue);
}

/** Generate a batch of math problems. */
export function generateMathSet(
  count: number,
  difficulty = 1,
): MathProblem[] {
  const out: MathProblem[] = [];
  for (let i = 0; i < count; i++) out.push(generateMathProblem(difficulty));
  return out;
}

/** Count how many problems in the batch were answered correctly. */
export function countCorrect(problems: MathProblem[]): number {
  let n = 0;
  for (const p of problems) {
    if (p.answeredCorrectly) n += 1;
  }
  return n;
}
